import type { ReactNode } from 'react';

/**
 * Full-bleed screen frame. Kiosk gets larger type throughout — the guest is
 * standing a metre back from a 49" portrait panel, not holding a phone.
 *
 * Kiosk content is clustered into a centred band rather than spread across the
 * full 1920px: on a panel that tall, anything pinned to the bottom edge is below
 * comfortable reach, and anything at the top is above eye line. The band height
 * is a guess until we have the mounting height — see docs/OPEN_ITEMS.md.
 *
 * Vertical strategy: content shrinks to fit where it can (see ScreenBody and the
 * media boxes, which flex down to a floor), and past that the frame scrolls.
 * It must never clip — this used to be `overflow-hidden`, which put the last
 * button of the result screen up to 800px below the fold with no way to reach
 * it, on every viewport including a stock iPhone.
 */
const KIOSK_BAND = 'max-h-[62rem]';

/**
 * Screens whose point IS the picture get a taller band.
 *
 * The reach band is 992px of a 1920px panel, which is right when the content is
 * type and buttons. It is wrong for the result preview: the clip is 9:16 on a
 * 9:16 panel, so its width is set entirely by how much height it is given, and
 * inside the reach band it came out 335px wide on a 1080px screen — a postage
 * stamp of the thing the guest just made.
 *
 * Still short of the full 1920 so the buttons under it stay off the bottom
 * edge. Like KIOSK_BAND this is a guess until the mounting height is measured —
 * see docs/OPEN_ITEMS.md.
 */
const KIOSK_BAND_MEDIA = 'max-h-[96rem]';

export function Screen({
  children,
  kiosk = false,
  band = 'reach',
  className = '',
  onPointerDown,
  backdrop,
  testId,
}: {
  children: ReactNode;
  kiosk?: boolean;
  /** Kiosk only. 'media' trades reach for picture size — see KIOSK_BAND_MEDIA. */
  band?: 'reach' | 'media';
  className?: string;
  onPointerDown?: () => void;
  /** Stable handle for the smoke test — see Button's `testId`. */
  testId?: string;
  /**
   * Art painted behind the content, which also switches the screen to the cream
   * ground the key art is printed on. One prop rather than a separate `ground`
   * flag, so the colour scheme cannot disagree with what is actually behind it.
   *
   * Screens over live camera pass nothing and stay dark, which is what the
   * storyboard shows for every one of them.
   */
  backdrop?: ReactNode;
}) {
  const art = backdrop !== undefined;

  const content = (
    <div
      className={[
        // h-[var(--app-h)], not `h-full min-h-[var(--app-h)]`. With only a minimum, tall content grows
        // the element past the viewport and the *document* scrolls, so
        // overflow-y-auto never engages and the last buttons sit below the fold
        // — the result screen measured 1056px inside an 844px viewport. A
        // definite height is what makes this frame scroll internally instead.
        'relative flex h-[var(--app-h)] min-h-[var(--app-h)] w-full flex-col items-center overflow-y-auto overflow-x-hidden',
        art ? 'text-ink' : 'bg-ink text-bone',
        // justify-start, not justify-between: ScreenBody already carries flex-1,
        // so the spacing is identical when the content fits — and when it does
        // not, a centred or space-between flex container in a scroll box clips
        // its overflow off the TOP, where it cannot be scrolled back to. That
        // put the result screen's heading above the viewport.
        kiosk ? 'justify-center px-16 py-16 text-2xl' : 'justify-start px-6 pt-safe pb-safe text-base',
        className,
      ].join(' ')}
    >
      {kiosk ? (
        <div
          className={`flex h-full min-h-0 w-full flex-col items-center justify-between ${
            band === 'media' ? KIOSK_BAND_MEDIA : KIOSK_BAND
          }`}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );

  if (!art) {
    return (
      <div onPointerDown={onPointerDown} data-testid={testId}>
        {content}
      </div>
    );
  }

  // The backdrop lives outside the scrolling element on purpose. An `absolute
  // inset-0` layer inside an `overflow-y-auto` box is positioned against the
  // scroll origin, so it scrolls off the top the moment the content is taller
  // than the frame — and on the kiosk the content band is 62rem inside 1920px.
  return (
    <div
      onPointerDown={onPointerDown}
      data-testid={testId}
      className="relative h-[var(--app-h)] w-full overflow-hidden bg-bone"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">{backdrop}</div>
      {content}
    </div>
  );
}

export function ScreenBody({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`flex min-h-0 flex-1 flex-col items-center justify-center gap-6 text-center ${className}`}
    >
      {children}
    </div>
  );
}

export function Title({ children, kiosk = false }: { children: ReactNode; kiosk?: boolean }) {
  return (
    <h1
      // text-current, so one Screen prop decides the ground and the type follows.
      className={`font-display uppercase leading-[0.95] tracking-tight text-current ${
        kiosk ? 'text-7xl' : 'text-4xl'
      }`}
    >
      {children}
    </h1>
  );
}

export function Body({ children, kiosk = false }: { children: ReactNode; kiosk?: boolean }) {
  return (
    <p
      // opacity rather than a /70 colour modifier: Tailwind cannot apply one to
      // `currentColor`, and the ground is now decided by the Screen above.
      className={`max-w-prose text-current opacity-70 ${kiosk ? 'text-3xl leading-snug' : 'text-base leading-relaxed'}`}
    >
      {children}
    </p>
  );
}
